/* global HTMLDivElement, KeyboardEvent */
// Onboarding first-run welcome modal (P2-12).
// Shows once on first visit: explain the app, offer sample data vs. empty start.
// Lead wires this into bento-shell.tsx first-run flow via isOnboardingDone().
import { useEffect, useRef } from "react";
import { Layers, X } from "lucide-react";
import { isOnboardingDone, setOnboardingDone } from "../lib/app-prefs";
import type { LifeOsApi } from "../lib/life-os-contract";
import { createEmptyState } from "../lib/storage";
import { useFocusTrap } from "../lib/focus-trap";

interface Props {
  api: LifeOsApi;
  onClose: () => void;
}

/**
 * OnboardingModal — first-run welcome.
 * Props consumed by the lead's bento-shell wiring:
 *   api     — live LifeOsApi (needed for replaceState on "empty start")
 *   onClose — callback to hide the modal
 *
 * Both choices mark onboarding done so this never reshows.
 * Guard: callers SHOULD check !isOnboardingDone() before rendering,
 * but the modal itself is safe to render regardless (buttons always call setOnboardingDone).
 */
export function OnboardingModal({ api, onClose }: Props) {
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef, true);

  // Escape = same as clicking X or backdrop (keep sample data)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setOnboardingDone(); onClose(); }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Defensive guard: if somehow already done, just close immediately.
  if (isOnboardingDone()) {
    onClose();
    return null;
  }

  function handleSampleData() {
    setOnboardingDone();
    onClose(); // keep current seed data as-is
  }

  function handleEmptyStart() {
    api.actions.replaceState(createEmptyState());
    setOnboardingDone();
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/40 p-4 backdrop-blur-sm"
      onClick={handleSampleData} // backdrop click = keep sample data
      role="dialog"
      aria-modal="true"
      aria-labelledby="onboarding-title"
    >
      <div
        ref={panelRef}
        className="relative w-full max-w-md rounded-3xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button — same as sample data choice */}
        <button
          className="absolute right-4 top-4 rounded-xl p-1.5 hover:bg-ink-100"
          onClick={handleSampleData}
          aria-label="Đóng"
        >
          <X className="h-4 w-4 text-ink-500" />
        </button>

        {/* Header */}
        <div className="mb-5 flex items-center gap-3">
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-brand-100 text-brand-600">
            <Layers className="h-6 w-6" />
          </span>
          <div>
            <h2 id="onboarding-title" className="font-display text-[20px] font-bold leading-tight">
              Personal Life OS
            </h2>
            <p className="text-[12px] text-ink-500">Chào mừng bạn đến</p>
          </div>
        </div>

        {/* Value proposition */}
        <p className="mb-6 text-[14px] leading-relaxed text-ink-700">
          <span className="font-semibold text-ink-900">Trung tâm điều hành cuộc sống</span>{" "}
          — việc, dự án, mục tiêu, thói quen, ghi chú, lịch, tiền.{" "}
          <span className="text-brand-600 font-medium">Capture một chỗ, liên kết dọc.</span>
        </p>

        {/* Feature pills */}
        <div className="mb-6 flex flex-wrap gap-2">
          {["Công việc", "Dự án", "Mục tiêu", "Thói quen", "Ghi chú", "Lịch", "Tài chính"].map(
            (label) => (
              <span
                key={label}
                className="rounded-full bg-brand-50 px-3 py-1 text-[12px] font-medium text-brand-700"
              >
                {label}
              </span>
            ),
          )}
        </div>

        {/* Choice prompt */}
        <p className="mb-3 text-[12px] font-bold uppercase tracking-widest text-ink-400">
          Bắt đầu với
        </p>

        {/* Action buttons */}
        <div className="flex flex-col gap-2.5">
          <button
            onClick={handleSampleData}
            className="flex w-full flex-col items-start rounded-2xl border-2 border-brand-200 bg-brand-50 px-4 py-3.5 text-left transition hover:border-brand-400 hover:bg-brand-100"
          >
            <span className="text-[14px] font-bold text-brand-800">Dùng dữ liệu mẫu</span>
            <span className="mt-0.5 text-[12px] text-ink-500">
              Có sẵn việc, dự án, mục tiêu, ghi chú mẫu để khám phá ngay
            </span>
          </button>

          <button
            onClick={handleEmptyStart}
            className="flex w-full flex-col items-start rounded-2xl border-2 border-ink-200 bg-white px-4 py-3.5 text-left transition hover:border-ink-300 hover:bg-ink-50"
          >
            <span className="text-[14px] font-bold text-ink-800">Bắt đầu trống</span>
            <span className="mt-0.5 text-[12px] text-ink-500">
              Trang trắng — chỉ giữ ví mặc định, tự thêm dữ liệu của bạn
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
